import React, { useEffect, useMemo, useRef } from 'react'
import {
  FieldPath,
  Form,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Group, Stack, Text } from '@mantine/core'
import { debounce } from 'lodash'
import { v4 as uuid } from 'uuid'
import * as yup from 'yup'

import { identificationPrompt } from '@/components/common/identificationPrompt'
import { AttendeeCard } from '@/components/Event/NewScript/components/common/AttendeeCard/AttendeeCard'
import { FormErrors } from '@/components/Event/NewScript/components/common/forms/FormErrors'
import { ActionButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionButton'
import { ActionContainer } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionContainer'
import { ActionText } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionText'
import { ActionTitle } from '@/components/Event/NewScript/components/common/ScriptTimeline/ActionTitle'
import { ContinueButton } from '@/components/Event/NewScript/components/common/ScriptTimeline/ContinueButton'
import { ScriptTimeline } from '@/components/Event/NewScript/components/common/ScriptTimeline/ScriptTimeline'
import { TimelineAction } from '@/components/Event/NewScript/components/common/ScriptTimeline/TimelineAction'
import { GuardianInput } from '@/components/Event/NewScript/components/Opening/GuardianInput'
import { OpeningVerbatim } from '@/components/Event/NewScript/components/Opening/OpeningVerbatim'
import { Stipulation } from '@/components/Event/NewScript/components/Opening/Stipulation'
import { SwearIn } from '@/components/Event/NewScript/components/Opening/SwearIn'
import { WitnessInput } from '@/components/Event/NewScript/components/Opening/WitnessInput'
import { useScriptFlowState } from '@/components/Event/NewScript/hooks/state/useScriptFlowState'
import { useStepActions } from '@/components/Event/NewScript/hooks/state/useStepActions'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import {
  EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS,
  EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED,
  EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED,
} from '@/components/Event/NewScript/schemas'
import { openingStepSchema } from '@/components/Event/NewScript/schemas/openingStepSchema'
import {
  getIdentityDocumentExemptAttendeeIds,
  getOpeningAttendeesAutosave,
  getOpeningAttendeeValidationPaths,
  shouldPreserveZoomParticipants,
} from '@/components/Event/NewScript/utils/attendees'
import {
  getEventTypeLongLabel,
  getWitnessEventTitle,
  isExaminationUnderOath,
} from '@/components/Event/NewScript/utils/event'
import { formatStepKey } from '@/components/Event/NewScript/utils/stepKey'
import { AttendeeRole, ScriptStep, TimestampType } from '@/constants'
import { useReporter } from '@/providers/UserProvider'
import { ScriptAttendee, ScriptStepProps } from '@/types'
import { getEventTimezone } from '@/utils/time'
import { isDR } from '@/utils/validations'

export type OpeningStepType = yup.InferType<typeof openingStepSchema>

export const OpeningStep = ({ event }: ScriptStepProps) => {
  const { state, actions, currentEvent, currentEventIndex } =
    useScriptFlowState()
  const {
    canContinue,
    completeStepAndContinue,
    completeAction,
    isActionCompleted,
  } = useStepActions(state.current_step, event)
  const reporter = useReporter()
  const {
    isAK,
    isAR,
    isAZ,
    isDE,
    isDC,
    isIL,
    isKY,
    isME,
    isND,
    isNE,
    isNM,
    isNY,
    isPA,
    isNV,
    isMS,
    isMT,
    isRI,
    isSD,
    isTN,
    isUT,
    isVA,
    isVT,
    isWV,
    isWY,
  } = useStatePolicies(event)
  const isWitnessLocationRequired = isAZ
  const identityDocumentExemptAttendeeIds =
    getIdentityDocumentExemptAttendeeIds(currentEvent!.attendees)

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(openingStepSchema),
    context: {
      [EVENT_FORM_CONTEXT_IS_IDENTITY_DOCUMENT_REQUIRED]: true,
      [EVENT_FORM_CONTEXT_IDENTITY_DOCUMENT_EXEMPT_ATTENDEE_IDS]:
        identityDocumentExemptAttendeeIds,
      [EVENT_FORM_CONTEXT_IS_WITNESS_LOCATION_REQUIRED]:
        isWitnessLocationRequired,
    },
    shouldFocusError: false,
    defaultValues: {
      attendees: currentEvent!.attendees ?? [],
      isRecordingStarted: currentEvent!.is_on_record ?? false,
    },
  })

  const { update, append } = useFieldArray({
    control: methods.control,
    name: 'attendees',
  })

  const isEUO = isExaminationUnderOath(event)
  const storeAttendeesRef = useRef<ScriptAttendee[]>()

  const {
    guardian,
    guardianIndex,
    witnessIndex,
    interpreterIndex,
    witness,
    counsels,
    interpreter,
    witnessFormatted,
    otherAttendees,
    otherAttendeesIndex,
  } = useMemo(() => {
    const witnessIndex = currentEvent!.attendees.findIndex(
      attendee => attendee.role === AttendeeRole.WITNESS,
    )
    const guardianIndex = currentEvent!.attendees.findIndex(
      attendee =>
        attendee.role === AttendeeRole.GUARDIAN && attendee.present === true,
    )
    const interpreterIndex = currentEvent!.attendees.findIndex(
      attendee => attendee.role === AttendeeRole.INTERPRETER,
    )

    const witnessValue =
      witnessIndex !== -1 ? currentEvent!.attendees[witnessIndex] : undefined
    const guardianValue =
      guardianIndex !== -1 ? currentEvent!.attendees[guardianIndex] : undefined

    const counsels = currentEvent!.attendees
      .map<[ScriptAttendee, number]>((attendee, index) => [attendee, index])
      .filter(
        ([attendee, _]) =>
          [
            AttendeeRole.INVESTIGATOR,
            AttendeeRole.OPPOSING_COUNSEL,
            AttendeeRole.STAFF_COUNSEL,
            AttendeeRole.DEFENDANT_ATTORNEY,
            AttendeeRole.PLAINTIFF_ATTORNEY,
            AttendeeRole.WITNESS_ATTORNEY,
          ].includes(attendee.role) && attendee.present,
      )

    const interpreterValue = currentEvent!.attendees.find(
      attendee => attendee.role === AttendeeRole.INTERPRETER,
    )

    const witnessFormattedValue = witnessValue
      ? witnessValue.name
      : '{witness name}'

    const presentAttendeeIds: string[] = [
      ...counsels.map(c => c[0]),
      interpreterValue,
      witnessValue,
      guardianValue,
    ]
      .filter(c => !!c)
      .map(c => c!.attendee_id)
    const otherAttendees: ScriptAttendee[] = currentEvent!.attendees.filter(
      a =>
        a.present &&
        !presentAttendeeIds.includes(a.attendee_id) &&
        !isDR(a.role),
    )
    const otherAttendeesIndex = otherAttendees.map(attendee =>
      currentEvent!.attendees.findIndex(
        a => a.attendee_id === attendee.attendee_id,
      ),
    )

    return {
      witnessIndex,
      witness: witnessValue,
      counsels,
      interpreter: interpreterValue,
      interpreterIndex,
      guardian: guardianValue,
      guardianIndex,
      witnessFormatted: witnessFormattedValue,
      otherAttendees,
      otherAttendeesIndex,
    }
  }, [currentEvent])

  useEffect(() => {
    // in case a new participant mapped to an existing attendee we need to update the form
    storeAttendeesRef.current = currentEvent!.attendees
    methods.setValue('attendees', currentEvent!.attendees)
  }, [methods, currentEvent])

  useEffect(() => {
    methods.setValue(
      'isRecordingStarted',
      currentEvent?.is_on_record ?? false,
      {
        shouldDirty: false,
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent?.is_on_record])

  const actionsRef = useRef(actions)
  actionsRef.current = actions
  const hasEditedZoomParticipants = useRef(false)

  const autosaveAttendee = useMemo(
    () =>
      debounce(async (changedFieldName: string) => {
        const validationPaths = getOpeningAttendeeValidationPaths(
          changedFieldName,
        ) as FieldPath<OpeningStepType>[] | undefined

        const isValid =
          !!validationPaths && (await methods.trigger(validationPaths))
        const attendeesUpdate = getOpeningAttendeesAutosave({
          changedFieldName,
          attendees: methods.getValues('attendees'),
          isValid,
          hasEditedZoomParticipants: hasEditedZoomParticipants.current,
          storeAttendees: storeAttendeesRef.current,
        })

        if (attendeesUpdate) {
          actionsRef.current.updateAttendees(attendeesUpdate)
        }
      }, 300),
    [methods],
  )

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (!name?.startsWith('attendees')) {
        return
      }

      if (!shouldPreserveZoomParticipants(name)) {
        hasEditedZoomParticipants.current = true
      }

      if (name === 'attendees') {
        const attendeesUpdate = getOpeningAttendeesAutosave({
          changedFieldName: name,
          attendees: (value.attendees ?? []) as ScriptAttendee[],
          isValid: true,
          hasEditedZoomParticipants: hasEditedZoomParticipants.current,
          storeAttendees: storeAttendeesRef.current,
        })

        if (attendeesUpdate) {
          actionsRef.current.updateAttendees(attendeesUpdate)
        }

        return
      }

      autosaveAttendee(name)
    })

    return () => {
      autosaveAttendee.flush()
      subscription.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.watch, autosaveAttendee])

  const validateAndCompleteAction = async (
    validationKeys: FieldPath<OpeningStepType>[],
    actionKey: string,
  ) => {
    const results = await Promise.all(
      validationKeys.map(key => methods.trigger(key)),
    )

    if (results.every(isValid => isValid)) {
      completeAction(actionKey)
    }
  }

  const addNewAttendee = () => {
    const attendee = {
      attendee_id: uuid(),
      name: '',
      email: '',
      role: AttendeeRole.OTHER,
      present: true,
      transcript_order: null,
      identity_document: null,
      zoom_participants: [],
      city: null,
      state: null,
    }
    append(attendee as unknown as ScriptAttendee)
    methods.trigger('attendees')
  }

  const isWitnessIdentityVerificationRequired =
    !!witness &&
    !identityDocumentExemptAttendeeIds.includes(witness.attendee_id)

  const witnessIdentityDocumentValidationPaths: FieldPath<OpeningStepType>[] =
    isWitnessIdentityVerificationRequired
      ? [
          `attendees.${witnessIndex}.identity_document.status`,
          `attendees.${witnessIndex}.identity_document.document_type`,
          `attendees.${witnessIndex}.identity_document.photo_id_verified`,
        ]
      : []

  return (
    <FormProvider {...methods}>
      <Form>
        <Stack align="flex-start" spacing="xl">
          <ScriptTimeline
            title={
              state.events.length > 1
                ? `${(currentEventIndex ?? 0) + 2}.1 Opening - ${getWitnessEventTitle(event, witness?.name)}`
                : '3. Opening'
            }
          >
            {/* Spotlight the witness in the Zoom app */}
            <TimelineAction
              actionKey="spotlight_the_wittness"
              title="Spotlight the witness in the Zoom app"
              onComplete={completeAction}
              completed={isActionCompleted('spotlight_the_wittness')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      It's critical to spotlight the witness in the Zoom app
                      before going on the record.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>
            </TimelineAction>

            {/* Go on the record and open deposition */}
            <TimelineAction
              actionKey="go_on_the_record"
              title={`Go on the record and open ${getEventTypeLongLabel(event)}`}
              onComplete={() =>
                validateAndCompleteAction(
                  ['isRecordingStarted'],
                  'go_on_the_record',
                )
              }
              completed={isActionCompleted('go_on_the_record')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    Is everyone ready to go on the record?
                  </ActionText>
                  <ActionContainer>
                    <Group>
                      <ActionButton
                        onClick={() => {
                          methods.setValue('isRecordingStarted', true)
                          methods.trigger('isRecordingStarted')
                          actions.addTimestamp({
                            isOnRecord: true,
                            timestampType: TimestampType.RECORDING_ON,
                            timezone: getEventTimezone(event),
                          })
                        }}
                        disabled={Boolean(currentEvent!.timestamps)}
                      >
                        Go on record
                      </ActionButton>
                      {currentEvent!.timestamps && (
                        <Text weight={700} size="xs">
                          You are now on the record.
                        </Text>
                      )}
                    </Group>
                  </ActionContainer>
                  {/* Form error */}
                  {!!methods.formState.errors.isRecordingStarted?.message && (
                    <FormErrors
                      errors={
                        methods.formState.errors.isRecordingStarted.message
                      }
                    />
                  )}
                </Stack>
              </ActionContainer>
              {!event.case.number && (
                <ActionContainer outer>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      Before going on record, please ask the counsel to provide{' '}
                      {isNY && isEUO ? 'index number' : 'case number'}.
                    </ActionText>
                  </ActionContainer>
                </ActionContainer>
              )}
              <ActionContainer outer>
                <ActionText mono>
                  <OpeningVerbatim
                    reporter={reporter}
                    event={event}
                    isAK={isAK}
                    isAR={isAR}
                    isAZ={isAZ}
                    isDE={isDE}
                    isDC={isDC}
                    isIL={isIL}
                    isKY={isKY}
                    isME={isME}
                    isND={isND}
                    isNE={isNE}
                    isNM={isNM}
                    isNY={isNY}
                    isPA={isPA}
                    isNV={isNV}
                    isMS={isMS}
                    isMT={isMT}
                    isRI={isRI}
                    isSD={isSD}
                    isTN={isTN}
                    isUT={isUT}
                    isVA={isVA}
                    isVT={isVT}
                    isWV={isWV}
                    isWY={isWY}
                  />
                </ActionText>
              </ActionContainer>
            </TimelineAction>

            {/* Identify the appearence of all present counsels for both parties */}
            <TimelineAction
              actionKey="identify_counsels"
              title="Identify the appearance of all present counsels for both parties"
              onComplete={key =>
                validateAndCompleteAction(
                  // Only validate counsel-specific fields, not identity_document
                  counsels.flatMap<FieldPath<OpeningStepType>>(([_, index]) => [
                    `attendees.${index}.name`,
                    `attendees.${index}.email`,
                    `attendees.${index}.role`,
                    `attendees.${index}.present`,
                    `attendees.${index}.zoom_participants`,
                  ]),
                  key,
                )
              }
              completed={isActionCompleted('identify_counsels')}
            >
              {counsels.map(([attendee, index]) => (
                <ActionContainer outer key={attendee.attendee_id}>
                  <Stack>
                    <ActionText mono>
                      {identificationPrompt(attendee.role)}
                    </ActionText>
                    <AttendeeCard
                      attendee={attendee}
                      event={event}
                      namePrefix={`attendees.${index}`}
                      allAttendees={currentEvent!.attendees}
                      onSubmitClick={value => update(index, value)}
                    />
                  </Stack>
                </ActionContainer>
              ))}
            </TimelineAction>

            {/* Let counsels for both parties stipulate */}
            <TimelineAction
              actionKey="let_counsels_stipulate"
              title="Let counsels for both parties stipulate"
              onComplete={completeAction}
              completed={isActionCompleted('let_counsels_stipulate')}
            >
              <ActionContainer outer>
                <Stack>
                  <ActionText mono>
                    <Stipulation reporter={reporter} />
                  </ActionText>
                  <ActionContainer>
                    <ActionTitle>Reminder</ActionTitle>
                    <ActionText>
                      Attorneys for both parties needs to stipulate to say
                      “yes”, or “I stipulate”.
                    </ActionText>
                  </ActionContainer>
                </Stack>
              </ActionContainer>
            </TimelineAction>

            {interpreter && (
              <TimelineAction
                actionKey="identify_interpreter"
                title="Identify the appearance of the interpreter and do swear-in"
                onComplete={key =>
                  validateAndCompleteAction(
                    [`attendees.${interpreterIndex}`],
                    key,
                  )
                }
                completed={isActionCompleted('identify_interpreter')}
              >
                <ActionContainer outer>
                  <Stack>
                    <ActionText mono>
                      Could the interpreter identify themselves and spell their
                      last name for the record?
                    </ActionText>
                    <AttendeeCard
                      attendee={interpreter}
                      event={event}
                      namePrefix={`attendees.${interpreterIndex}`}
                      allAttendees={currentEvent!.attendees}
                      onSubmitClick={value => update(interpreterIndex, value)}
                    />
                  </Stack>
                </ActionContainer>
                <ActionContainer outer>
                  <ActionText mono>
                    Please raise your right hand. Do you solemnly swear that you
                    will interpret the following questions from English into{' '}
                    {`{language}`}, and the answers from {`{language}`} into
                    English to the best of your ability?
                  </ActionText>
                </ActionContainer>
              </TimelineAction>
            )}

            {guardian && witness && (
              <TimelineAction
                actionKey="identify_guardian"
                title="Identify the appearance of guardian for the witness"
                onComplete={key =>
                  validateAndCompleteAction(
                    [
                      `attendees.${guardianIndex}`,
                      `attendees.${guardianIndex}.identity_document.status`,
                      `attendees.${guardianIndex}.identity_document.document_type`,
                      `attendees.${guardianIndex}.identity_document.photo_id_verified`,
                    ],
                    key,
                  )
                }
                completed={isActionCompleted('identify_guardian')}
              >
                <GuardianInput
                  event={event}
                  namePrefix={`attendees.${guardianIndex}`}
                  guardian={guardian}
                  witnessName={witnessFormatted}
                  allAttendees={currentEvent!.attendees}
                  attendeeChanged={value => update(guardianIndex, value)}
                />
              </TimelineAction>
            )}

            {witness && (
              <TimelineAction
                actionKey="identify_wittness"
                title="Identify the witness"
                onComplete={key =>
                  validateAndCompleteAction(
                    [
                      `attendees.${witnessIndex}`,
                      ...witnessIdentityDocumentValidationPaths,
                      `attendees.${witnessIndex}.city`,
                      `attendees.${witnessIndex}.state`,
                    ],
                    key,
                  )
                }
                completed={isActionCompleted('identify_wittness')}
              >
                <WitnessInput
                  event={event}
                  namePrefix={`attendees.${witnessIndex}`}
                  witness={witness}
                  allAttendees={currentEvent!.attendees}
                  attendeeChanged={value => update(witnessIndex, value)}
                  isWitnessLocationRequired={isWitnessLocationRequired}
                  isIdentityVerificationRequired={
                    isWitnessIdentityVerificationRequired
                  }
                />
              </TimelineAction>
            )}
            {witness && (
              <TimelineAction
                actionKey="swear_in_witness"
                title="Swear in the witness"
                onComplete={completeAction}
                completed={isActionCompleted('swear_in_witness')}
              >
                <SwearIn witness={witness} event={event} />
              </TimelineAction>
            )}

            <TimelineAction
              actionKey="other_parties_identification"
              title="Other parties identification"
              onComplete={key =>
                validateAndCompleteAction(
                  otherAttendees.map<FieldPath<OpeningStepType>>(
                    (_, index) => `attendees.${otherAttendeesIndex[index]}`,
                  ),
                  key,
                )
              }
              completed={isActionCompleted('other_parties_identification')}
            >
              <ActionContainer outer>
                <ActionText mono>
                  Are there any other attendees that haven’t identified
                  themselves?
                </ActionText>
                {otherAttendees.map((attendee, index) => (
                  <AttendeeCard
                    key={attendee.attendee_id}
                    attendee={attendee}
                    event={event}
                    namePrefix={`attendees.${otherAttendeesIndex[index]}`}
                    allAttendees={currentEvent!.attendees}
                    onSubmitClick={value =>
                      update(otherAttendeesIndex[index], value)
                    }
                  />
                ))}
                <Button
                  variant="subtle"
                  compact
                  styles={{ root: { paddingLeft: 0, paddingRight: 0 } }}
                  onClick={addNewAttendee}
                >
                  + Add Additional Attendee
                </Button>
              </ActionContainer>
            </TimelineAction>

            <ContinueButton
              event={event}
              label="Continue to Questioning"
              canContinue={canContinue()}
              step={ScriptStep.OPENING}
              handleClick={() =>
                completeStepAndContinue(
                  formatStepKey(ScriptStep.QUESTIONING, currentEventIndex),
                )
              }
            />
          </ScriptTimeline>
        </Stack>
      </Form>
    </FormProvider>
  )
}
